import {
    contextRepository,
    eventRepository,
    gameRepository,
    seasonRepository
} from "../storage/storage";
import FootballGameContext from "../sports/football/context/football-game-context";
import ManualGameClock from "../sports/common/utils/manual-game-clock";
import ClockFactory from "../sports/common/utils/clock-factory";
import TennisGameContext from "../sports/tennis/context/tennis-game-context";
import FlagFootballGameContext from "../sports/flag-football/context/flag-football-game-context";
import SportService from "./sport-service";
import SportCodes from "../sports/common/sport-codes";
import { migrateClockConfig } from "../sports/football/utils/field-config-utils";
import GamedayApiService from "./gameday-api-service";
import SeasonService from "./season-service";
import ParticipationStyle from "../sports/football/enums/participation-style";

class ContextService {
    constructor() {
        this.contextFactory = {
            [SportCodes.Football]: async (values) =>
                this._createFootballContext(values),
            [SportCodes.FlagFootball]: async (values) =>
                this._createFlagFootballContext(values),
            [SportCodes.Tennis]: async (values) =>
                this._createTennisContext(values),
            [SportCodes.WomenTennis]: async (values) =>
                this._createTennisContext(values)
        };
        this.toggleMap = {
            [SportCodes.Football]: this._toggleFootballDefaults,
            [SportCodes.FlagFootball]: this._toggleFootballDefaults,
            [SportCodes.Tennis]: this._toggleTennisDefaults,
            [SportCodes.WomenTennis]: this._toggleTennisDefaults
        };
    }

    async createContext(gameId) {
        const game = await gameRepository.getById(gameId);
        const event = await eventRepository.getById(game.eventId);
        // Season may not exist locally when accessing Event from CMS, perform remote lookup as fallback
        let season =
            (await seasonRepository.getBySeasonId(event.seasonId)) ||
            (await GamedayApiService.getSeason(event.seasonId));

        await SeasonService.refreshFromRemote(event.seasonId);
        season =
            (await seasonRepository.getBySeasonId(event.seasonId)) || season;

        const { teams, sportId } = event;
        const { homeTeam, awayTeam } = teams;
        const { ruleSetId } = season;
        // The event's saved settings override the season default — same
        // precedence as EventService.getGameConfigurationOrDefault, so the
        // context honors what the settings screen shows for the event.
        // migrateClockConfig lifts legacy general.clockType saves into the
        // clock section the clock bootstrapping reads.
        const gameConfiguration = migrateClockConfig(
            event.gameConfiguration ||
                season.gameConfiguration ||
                SportService.resolveDefaultConfig(sportId, ruleSetId)
        );

        await this.contextFactory[sportId]({
            game,
            awayTeam,
            homeTeam,
            gameConfiguration,
            ruleSetId
        });

        return this.getGameContext(gameId);
    }

    /**
     * Shared football/flag football clock bootstrapping: build the clock
     * from the config's runningClock flag, tag the context's clockType, and
     * start it. ManualGameClock.start() is a no-op; a real running clock
     * stays paused until the crew starts it from the scoreboard.
     */
    _initializeGameClock(gameContext, gameConfiguration) {
        const clockType = gameConfiguration.clock?.runningClock
            ? "running"
            : "manual";
        gameContext.gameClock = ClockFactory.create({
            type: clockType,
            limitSeconds: gameConfiguration.general.minutesByPeriod * 60
        });
        gameContext.clockType = clockType;
        if (clockType !== "running") {
            gameContext.gameClock.start();
        }
    }

    async _createFootballContext({
        game,
        awayTeam,
        homeTeam,
        gameConfiguration,
        ruleSetId
    }) {
        const gameContext = new FootballGameContext({
            gameId: game.id,
            ruleSetId,
            config: gameConfiguration,
            fieldPosition: gameConfiguration.spots.driveStartingYardline,
            awayTeamId: awayTeam.id,
            homeTeamId: homeTeam.id
        });
        this._initializeGameClock(gameContext, gameConfiguration);
        gameContext.startPeriod();

        await this._setInitialFootballContextValues(
            gameContext,
            awayTeam.teamId,
            homeTeam.teamId
        );

        return this.saveContext(gameContext);
    }

    async _createFlagFootballContext({
        game,
        awayTeam,
        homeTeam,
        gameConfiguration,
        ruleSetId
    }) {
        const gameContext = new FlagFootballGameContext({
            gameId: game.id,
            ruleSetId,
            config: gameConfiguration,
            fieldPosition: gameConfiguration.spots.driveStartingYardline,
            awayTeamId: awayTeam.id,
            homeTeamId: homeTeam.id
        });
        this._initializeGameClock(gameContext, gameConfiguration);
        gameContext.startPeriod();

        await this._setInitialFootballContextValues(
            gameContext,
            awayTeam.teamId,
            homeTeam.teamId
        );

        return this.saveContext(gameContext);
    }

    async _createTennisContext({
        game,
        awayTeam,
        homeTeam,
        gameConfiguration,
        ruleSetId
    }) {
        const gameContext = new TennisGameContext({
            gameId: game.id,
            ruleSetId,
            config: gameConfiguration,
            gameClock: new ManualGameClock(12 * 60),
            awayTeamId: awayTeam.id,
            homeTeamId: homeTeam.id
        });

        gameContext.gameClock.start();

        await this._setInitialTennisContextValues(
            gameContext,
            awayTeam.teamId,
            homeTeam.teamId
        );

        return this.saveContext(gameContext);
    }

    async _setInitialFootballContextValues(
        gameContext,
        externalAwayId,
        externalHomeId
    ) {
        const { awayTeamId, homeTeamId } = gameContext;

        await this._setDefaultFootballPlayerValues(
            gameContext.getTeamContext(awayTeamId),
            externalAwayId
        );

        await this._setDefaultFootballPlayerValues(
            gameContext.getTeamContext(homeTeamId),
            externalHomeId
        );
    }

    async _setInitialTennisContextValues(
        gameContext,
        externalAwayId,
        externalHomeId
    ) {
        const { awayTeamId, homeTeamId } = gameContext;

        await this._setDefaultTennisPlayerValues(
            gameContext.getTeamContext(awayTeamId),
            externalAwayId
        );

        await this._setDefaultTennisPlayerValues(
            gameContext.getTeamContext(homeTeamId),
            externalHomeId
        );
    }

    async _setDefaultTennisPlayerValues(teamContext, externalId) {
        const players = await GamedayApiService.getPlayers(externalId);
        const playerIds = players.map((p) => p.id);

        this._toggleTennisDefaults(teamContext, playerIds);
    }

    async _setDefaultFootballPlayerValues(teamContext, externalId) {
        const players = await GamedayApiService.getPlayers(externalId);
        this._toggleFootballDefaults(
            teamContext,
            players.map((p) => p.id)
        );
    }

    _toggleTennisDefaults(teamContext, playerIds) {
        teamContext.single.push(...playerIds);
        teamContext.doubles.push(...playerIds);
    }

    _toggleFootballDefaults(teamContext, playerIds, gameConfig) {
        const { participationStyle } = gameConfig?.general || {};
        if (participationStyle !== ParticipationStyle.Continuous.code) {
            teamContext.participants.push(...playerIds);
        }
    }

    async toggleDefaults(gameId, teamId, playerIds) {
        const { sportCode } = await gameRepository
            .where({ id: gameId })
            .first();
        const gameContext = await this.getGameContext(gameId);
        const teamContext = gameContext.getTeamContext(teamId);
        this.toggleMap[sportCode](
            teamContext,
            playerIds,
            gameContext.gameConfig
        );
        return this.updateContext(gameContext);
    }

    async saveContext(context) {
        context.id = await contextRepository.add(context);

        return this.getContext(context.id);
    }

    async updateContext(context) {
        context.id = await contextRepository.put(context);

        return this.getContext(context.id);
    }

    async deleteContext(contextId) {
        return await contextRepository.delete(contextId);
    }

    async getContext(contextId) {
        return await contextRepository.getById(contextId);
    }

    async getGameContext(gameId) {
        let [context] = await contextRepository
            .where("gameId")
            .equals(gameId)
            .toArray();

        // Read-time heal: re-derive the clock from the context's own config
        // so rows drifted by older writers (sync blobs, pre-reconciler
        // saves) self-correct. In-memory only — persists on the next
        // natural updateContext. Idempotent; tennis no-ops (no clock
        // section in its config).
        if (context?.gameClock) {
            ClockFactory.applyClockConfig(context, context.gameConfig);
        }

        return context;
    }

    async startPeriod(gameId) {
        const gameContext = await this.getGameContext(gameId);
        gameContext.startPeriod();

        return this.updateContext(gameContext);
    }

    async setSeconds(seconds, gameId) {
        const gameContext = await this.getGameContext(gameId);
        gameContext.gameClock.setSeconds(seconds);

        return this.updateContext(gameContext);
    }

    /**
     * Pause the game clock. If byPlaySequence is true, marks the pause as
     * play-sequence-initiated so resumeGameClock can auto-resume later
     * while preserving manual pauses.
     */
    async pauseGameClock(gameId, { byPlaySequence = false } = {}) {
        const gameContext = await this.getGameContext(gameId);
        if (!gameContext || !gameContext.gameClock) return gameContext;

        if (!gameContext.gameClock.isPaused()) {
            gameContext.gameClock.pause();
            if (byPlaySequence) {
                gameContext.pausedByPlaySequence = true;
            }
        }
        return this.updateContext(gameContext);
    }

    /**
     * Resume the game clock. If onlyIfByPlaySequence is true, resumes only
     * when the pause was play-sequence-initiated (preserves manual pauses).
     * An untimed overtime (fbOtDisabled) keeps it paused (PSTATS-1268).
     */
    async resumeGameClock(gameId, { onlyIfByPlaySequence = false } = {}) {
        const gameContext = await this.getGameContext(gameId);
        if (!gameContext || !gameContext.gameClock) return gameContext;
        if (gameContext.fbOtDisabled) return gameContext;

        if (onlyIfByPlaySequence && !gameContext.pausedByPlaySequence) {
            return gameContext;
        }

        if (gameContext.gameClock.isPaused()) {
            gameContext.gameClock.continue();
        }
        gameContext.pausedByPlaySequence = false;
        return this.updateContext(gameContext);
    }

    /**
     * Reconcile a game's clock with its configuration and persist the
     * result. The config is the only authority for the running/manual
     * choice (missing runningClock = manual); gameContext.clockType and
     * the clock instance are derived from it via
     * ClockFactory.applyClockConfig. Idempotent.
     * @param {string} gameId
     * @param {object} [gameConfiguration] - defaults to the context's own
     *  gameConfig (the live snapshot — the post-start authority)
     */
    async reconcileClock(gameId, gameConfiguration) {
        const gameContext = await this.getGameContext(gameId);
        if (!gameContext) return gameContext;

        ClockFactory.applyClockConfig(gameContext, gameConfiguration);
        return this.updateContext(gameContext);
    }

    async setOvertimeNotification(gameId, value) {
        const gameContext = await this.getGameContext(gameId);

        return this.updateContext(
            Object.assign(gameContext, { overtimeNotification: value })
        );
    }

    async swapTeams(gameId, swap) {
        const gameContext = await this.getGameContext(gameId);

        gameContext.swapTeams = swap;
        gameContext.triggerSwapTeams = false;

        return this.updateContext(gameContext);
    }

    /**
     * Update the orderPlayersByPosition FootballGameContext property, using the provided gameId
     * @param {string} gameId
     * @param {boolean} order
     * @returns {Promise<FootballGameContext>}
     */
    async orderPlayersByPosition(gameId, order) {
        const gameContext = await this.getGameContext(gameId);

        gameContext.orderPlayersByPosition = order;

        return this.updateContext(gameContext);
    }

    async keepSwappingTeams(gameId, swap) {
        const gameContext = await this.getGameContext(gameId);

        gameContext.keepSwappingTeams = swap;

        return this.updateContext(gameContext);
    }

    async confirmPeriodNotification(gameId) {
        const gameContext = await this.getGameContext(gameId);
        gameContext.triggerNewPeriodNotification = false;

        return this.updateContext(gameContext);
    }

    async updateContextScore(event) {
        const game = await gameRepository.where({ eventId: event.id }).first();

        if (game) {
            const gameContext = await this.getGameContext(game.id);
            const { score } = event;
            gameContext.score = [Number(score.home), Number(score.away)];

            return this.updateContext(gameContext);
        }

        return null;
    }
}

export default new ContextService();
